import { useEffect, useMemo, useState } from "react";
import { KpiCard } from "../components/ui/KpiCard";
import { CoberturaHint } from "../components/ui/CoberturaHint";
import { DataSourceBadge } from "../components/ui/DataSourceBadge";
import { CurvaStaleBadge } from "../components/ui/CurvaStaleBadge";
import { SixDuCard } from "../components/financeiro/SixDuCard";
import { CalendarioCaixaCard } from "../components/financeiro/CalendarioCaixaCard";
import {
  ROTULOS_ESPELHO_OMIE,
  type RotulosFonte30d,
  FluxoBarChart30d,
  FluxoBarChart6m,
  computeRows30d,
  _menorSaldo30d,
} from "../components/financeiro/FluxoBarChart";
import { FluxoExcelButton } from "../components/financeiro/FluxoExcelButton";
import { montarExcelInput } from "../components/financeiro/fluxoExport";
import { LiquidacoesNetTabs } from "../components/financeiro/LiquidacoesNetTabs";
import { PosicaoAbertaSection } from "../components/financeiro/PosicaoAbertaSection";
import { PrevisaoNaoFirmeNotas } from "../components/financeiro/PrevisaoNaoFirmeNotas";
import { SaldoAtualCard } from "../components/financeiro/SaldoAtualCard";
import {
  CompromissadaFluxoLinha,
  composicaoAncoraFluxo,
} from "../components/financeiro/CompromissadaFluxoLinha";
import { SaldoProjetadoTable } from "../components/financeiro/SaldoProjetadoTable";
import { getFluxo } from "../api/client";
import type { AsOfMeta } from "../api/types";
import type { FluxoRow } from "../api/types";
import { useProximo6du } from "../hooks/useProximo6du";
import { useCalendarioCaixa } from "../hooks/useCalendarioCaixa";
import { useCalendario6dus } from "../hooks/useCalendario6dus";
import { usePosicaoAbertaMtm6m } from "../hooks/usePosicaoAbertaMtm6m";
import { useFluxoV2 } from "../hooks/useFluxoV2";
import { usePrevisaoVencida } from "../hooks/usePrevisaoVencida";
import { PrevisaoVencidaCard } from "../components/financeiro/PrevisaoVencidaCard";
import { ExistenciaCpCard } from "../components/financeiro/ExistenciaCpCard";
import { useSaldoV2 } from "../hooks/useSaldoV2";
import { useZeusStatus } from "../hooks/useZeusStatus";
import { ymdIso } from "../utils/dateBr";
import type { SixDuNav } from "../components/financeiro/SixDuCard";
import {
  apiRowsToDiario,
  apiRowsToKpis,
  apiRowsToMensal,
  fluxo30Dias,
  fluxoKpis,
  gerarFluxoMensal,
  kpis6DuFromApi,
  projecaoSaldo6Meses,
  proximoSeisDu,
  saldoAtualTotal,
} from "../utils/mockData";
import { fmtBRL, fmtBRLintSigned } from "../utils/formatters";

type DataSource = "api" | "mock";

interface FluxoState {
  rows: FluxoRow[] | null;
  source: DataSource;
  loading: boolean;
}

// Janela de fetch: cobre o `fluxo30Dias` (10d atrás → 20d à frente) E o
// `gerarFluxoMensal(6)` (próximos 6 meses) numa única chamada — evita 2
// roundtrips e mantém a UI consistente.
// Task #170: janela ampliada pra cobrir os 6dus navegaveis (ate -6 / +12 a
// partir do oficial ≈ -7mo .. +13mo). SixDuCard filtra os KPIs por data
// client-side via kpis6DuFromApi, entao a data selecionada precisa estar
// dentro do range buscado. Charts/tabela 6m continuam fatiando o que usam.
function buildJanela(hoje = new Date()): { inicio: string; fim: string } {
  const inicio = new Date(hoje.getFullYear(), hoje.getMonth() - 7, 1);
  const fim = new Date(hoje.getFullYear(), hoje.getMonth() + 14, 0);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  return { inicio: iso(inicio), fim: iso(fim) };
}

export function FinanceiroFluxoPage() {
  // #40: badge de curva stale (as-of do dado, nao idade do cron) — o Fluxo
  // marca o 6DU pela curva forward (posicao aberta MtM), afetado no incidente.
  const curvaDs = useZeusStatus().data?.datasets.curva_forward ?? null;
  const [state, setState] = useState<FluxoState>({
    rows: null,
    source: "api",
    loading: true,
  });
  // #60: as-of do payload do fluxo (alimenta a tabela de 6 meses e o gráfico)
  const [fluxoAsOf, setFluxoAsOf] = useState<AsOfMeta | null | undefined>(undefined);

  useEffect(() => {
    let cancel = false;
    const { inicio, fim } = buildJanela();
    getFluxo(inicio, fim)
      .then((resp) => {
        if (cancel) return;
        // #60: as-of do DADO servido (snapshot/live) viaja com as rows
        setFluxoAsOf(resp.as_of ?? null);
        setState({ rows: resp.rows, source: "api", loading: false });
      })
      .catch(() => {
        // Auth errors, network, 5xx — qualquer falha cai no mock pra não
        // travar a apresentação. O badge avisa que é demo.
        if (cancel) return;
        setFluxoAsOf(null); // #60: sem resposta = as-of desconhecido, declarado
        setState({ rows: null, source: "mock", loading: false });
      });
    return () => {
      cancel = true;
    };
  }, []);

  // SixDuCard: agrega entradas/saídas reais do dia do próximo 6DU.
  //
  // Task #61: a DATA do próximo 6du vem do calendário CCEE oficial via
  // useProximo6du (com fallback heurístico OFFLINE).
  //
  // Fix #156: agora `kpis6DuFromApi` recebe a data oficial direto em
  // vez de recomputar via heurística — antes a heurística ignorava
  // feriado móvel (Corpus Christi etc.), buscava 1 dia errado, achava
  // 0 trades e caía no mock R$ 14,2M / R$ 9,8M. Quando rows não tem
  // trade na data correta, `temDadosReais=false` e o card renderiza
  // empty state "—" honesto, não mock.
  //
  // Enquanto useProximo6du não resolveu, usa heurística como placeholder
  // visual (pra não piscar a data) mas mantém temDadosReais=false.
  const proximo6du = useProximo6du();
  const saldoV2 = useSaldoV2();
  // 31/08 (call do Veiga): a âncora do Fluxo passa a ser COMPOSTA —
  // disponível + compromissada Itaú (estado ok). Ausência de dado NÃO vira
  // movimento: estado != ok ⇒ soma só o disponível e a LINHA declara parcial.
  const compFluxo = composicaoAncoraFluxo(saldoV2.data);
  const saldoAtual = compFluxo.total ?? saldoAtualTotal;

  // Task #170: navegação prev/next entre 6dus. `data6duSelecionada` é a data
  // que o Card mostra; null = no próximo oficial (default). A tabela detalhada
  // (LiquidacoesNetTabs) navega junto; charts e tabela "Saldo 6 meses" NÃO.
  const calendario6dus = useCalendario6dus();
  const [data6duSelecionada, setData6duSelecionada] = useState<Date | null>(null);
  const officialDate = proximo6du.info?.data ?? null;
  const data6duAtual = data6duSelecionada ?? officialDate;
  // #244: o Calendário de Caixa ancora no 6du NAVEGADO (1ª linha = 6du do card).
  // Data vem do calendário CCEE (data6duAtual) — nunca calculada localmente
  // (regra de ferro #156/#51). null (carregando) → undefined = ciclo corrente.
  const ancora6duIso = data6duAtual ? ymdIso(data6duAtual) : undefined;

  // Task #207: a Tabela "Saldo projetado 6 meses" + a coluna MtM ancoram no
  // mês de CAIXA do próximo 6du OFICIAL (mês civil em que o dt_6du cai), não
  // em hoje+1. Isso alinha a 1ª linha da Tabela com o Card (que ancora no
  // próximo 6du) e inclui na projeção o 6du iminente do mês corrente — antes,
  // na janela dia 1º→6du, a Tabela começava no mês seguinte e omitia esse 6du.
  // A data vem do calendário CCEE (useProximo6du) — NUNCA calculada localmente
  // (paridade #156/#158). Fallback null enquanto carrega → apiRowsToMensal e o
  // hook caem no default legado (hoje+1), pra não piscar a janela.
  const mesInicioCaixa = useMemo(
    () => (officialDate ? new Date(officialDate.getFullYear(), officialDate.getMonth(), 1) : null),
    [officialDate],
  );
  const mesInicioCaixaIso = mesInicioCaixa ? ymdIso(mesInicioCaixa) : undefined;

  // 05/08 FONTE ÚNICA NA TELA: o card 6DU deriva do MESMO dado LIVE do
  // Calendário de Caixa (linha eh_6du_atual do ciclo ancorado — mesma request,
  // cache por âncora). O /api/fluxo legado serve o precompute regravado 1×/dia
  // e APODRECIA intradia: em 05/08 entraram +R$1,54mi de compras pós-snapshot
  // e o card mostrou +3,05mi contra +1,59mi do acumulado NA MESMA tela (2ª vez
  // no dia). Legado fica só de fallback enquanto o calendário carrega.
  const calCaixa = useCalendarioCaixa(ancora6duIso);
  const dia6duLive = useMemo(() => {
    const alvo = data6duAtual ? ymdIso(data6duAtual) : null;
    const d = calCaixa.data.dias.find((x) => x.eh_6du_atual);
    return alvo && d && d.data === alvo ? d : null;
  }, [calCaixa.data, data6duAtual]);
  // Fallback NUNCA silencioso (05/08): legado com o calendário em ERRO (não
  // loading) = a fonte única não está rodando — loga loud pro console/sessão
  // e o card carrega data-fonte="fluxo_legado" (observável em teste/DOM).
  useEffect(() => {
    if (!dia6duLive && !calCaixa.isLoading && calCaixa.error) {
      console.warn(
        "[6DU] card em FALLBACK legado (calendário com erro) — o número pode "
        + "apodrecer intradia; fonte única fora do ar", calCaixa.error,
      );
    }
  }, [dia6duLive, calCaixa.isLoading, calCaixa.error]);

  const info = useMemo(() => {
    const alvo = data6duAtual;
    const dataEff = alvo ?? proximoSeisDu().data;
    if (alvo && dia6duLive) {
      const entradas = Number(dia6duLive.entra);
      const saidas = Math.abs(Number(dia6duLive.sai));
      return {
        info: {
          data: alvo,
          dataIso: ymdIso(alvo),
          entradas,
          saidas,
          liquido: entradas - saidas,
        },
        temDadosReais: true,
      };
    }
    const real = state.rows && alvo ? kpis6DuFromApi(state.rows, alvo) : null;
    if (real) {
      return { info: real, temDadosReais: true };
    }
    return {
      info: {
        data: dataEff,
        dataIso: ymdIso(dataEff),
        entradas: 0,
        saidas: 0,
        liquido: 0,
      },
      temDadosReais: false,
    };
  }, [state.rows, data6duAtual, dia6duLive]);

  // Task #170 follow-up: o KPI topo "Saldo projetado pós-6DU" reflete SEMPRE
  // o 6du OFICIAL (estado real da empresa), independente da navegação do Card.
  // O `info` acima acompanha `data6duSelecionada` (Card + tabela detalhada);
  // o KPI topo NÃO pode — senão o textinho "+R$ X na liquidação" mostraria o
  // líquido do 6du navegado enquanto o VALOR (kpis.saldoProjPosSeisDu, sempre
  // oficial via proximo6du.info?.data) mostra o oficial → inconsistência.
  // Derivar do mesmo `officialDate`/`state.rows` garante:
  //   value (kpis.saldoProjPosSeisDu) = saldoAtual + liquidoOficial6du.liquido
  // ou seja, Saldo Atual + textinho = Saldo Projetado Pós-6DU (consistente).
  const liquidoOficial6du = useMemo(() => {
    // 05/08 fonte única: quando o ciclo exibido É o oficial, o líquido vem da
    // MESMA linha live do calendário que alimenta o card — value e sub do KPI
    // derivam do mesmo número (contrato #170: valor − líquido = saldo atual).
    if (!data6duSelecionada && dia6duLive) {
      return {
        liquido: Number(dia6duLive.entra) + Number(dia6duLive.sai),
        temDadosReais: true,
      };
    }
    const real = state.rows && officialDate ? kpis6DuFromApi(state.rows, officialDate) : null;
    return real
      ? { liquido: real.liquido, temDadosReais: true }
      : { liquido: 0, temDadosReais: false };
  }, [state.rows, officialDate, data6duSelecionada, dia6duLive]);

  const nav: SixDuNav | undefined = useMemo(() => {
    const cal = calendario6dus.calendario;
    if (cal.length === 0 || !data6duAtual) return undefined;
    const alvoIso = ymdIso(data6duAtual);
    const idx = cal.findIndex((c) => ymdIso(c) === alvoIso);
    const officialIso = officialDate ? ymdIso(officialDate) : null;
    const hojeIso = ymdIso(new Date());
    const relacao: SixDuNav["relacao"] =
      officialIso && alvoIso === officialIso
        ? "proximo"
        : alvoIso < hojeIso
          ? "historico"
          : "futuro";
    return {
      relacao,
      canPrev: idx > 0,
      canNext: idx >= 0 && idx < cal.length - 1,
      onPrev: () => {
        if (idx > 0) setData6duSelecionada(cal[idx - 1]);
      },
      onNext: () => {
        if (idx >= 0 && idx < cal.length - 1) setData6duSelecionada(cal[idx + 1]);
      },
      onVoltarProximo: () => setData6duSelecionada(null),
    };
  }, [calendario6dus.calendario, data6duAtual, officialDate]);

  // Task #206: mes_ref do fornecimento que liquida no 6du exibido — alimenta
  // o KPI "Posição aberta" do Card pra ele VARIAR na navegação prev/next
  // (antes congelava no próximo oficial: o hook nunca recebia data e batia
  // sempre no default do endpoint). Regras:
  //  - histórico (dt_6du < hoje) → null: KPI mostra "—" sem fetch (foco do
  //    #206 são os 6dus FUTUROS; histórico é default conservador);
  //  - sem entrada no calendário (ainda carregando) → undefined → default do
  //    endpoint (próximo oficial), comportamento legado;
  //  - caso normal → "YYYY-MM-01" (filtro mes_ref do /api/fluxo/posicao-aberta).
  const mesRefSelecionado = useMemo<string | null | undefined>(() => {
    if (!data6duAtual) return undefined;
    const alvoIso = ymdIso(data6duAtual);
    if (alvoIso < ymdIso(new Date())) return null;
    const mesRef = calendario6dus.mesRefByData[alvoIso];
    return mesRef ? `${mesRef}-01` : undefined;
  }, [data6duAtual, calendario6dus.mesRefByData]);

  const diario = useMemo(
    () => (state.rows ? apiRowsToDiario(state.rows, proximo6du.info?.data) : fluxo30Dias()),
    [state.rows, proximo6du.info],
  );
  const mensal = useMemo(
    () =>
      state.rows
        ? apiRowsToMensal(state.rows, 6, new Date(), saldoAtual, mesInicioCaixa ?? undefined)
        : gerarFluxoMensal(6),
    [state.rows, saldoAtual, mesInicioCaixa],
  );
  const kpis = useMemo(() => {
    const base = state.rows
      ? apiRowsToKpis(state.rows, new Date(), proximo6du.info?.data, saldoAtual)
      : fluxoKpis();
    // 05/08: o VALUE do KPI deriva do MESMO liquidoOficial6du do sub (que já
    // prefere o calendário live) — identidade do #170 por construção:
    // saldoProjPosSeisDu = saldoAtual + líquido exibido no textinho.
    if (liquidoOficial6du.temDadosReais) {
      return { ...base, saldoProjPosSeisDu: saldoAtual + liquidoOficial6du.liquido };
    }
    return base;
  }, [state.rows, proximo6du.info, saldoAtual, liquidoOficial6du]);
  const projecao = useMemo(
    () => projecaoSaldo6Meses(saldoAtual, mensal),
    [mensal, saldoAtual],
  );

  // Toggle de janela do grafico Fluxo (30/60/90 dias). Default 30. Troca
  // re-fetcha /api/v2/fluxo?dias=N via useFluxoV2 (cache chaveado por janela) e
  // re-renderiza o grafico (eixo X D+0..D+N + marcadores 6DU no range).
  const [janelaDias, setJanelaDias] = useState<30 | 60 | 90>(30);

  // /api/v2/fluxo alimenta o grafico 30/60/90d (series Omie/CP) e o saldo
  // pos-6DU. (Os cards Entradas/Saidas do mes sairam no UX 03/07 item 4.)
  const fluxoV2 = useFluxoV2(janelaDias);
  const v2Ok = !!fluxoV2.data && !fluxoV2.error;
  // #149/#154 NIVEL FORA DA SERIE: titulos pendentes com previsao < hoje nao
  // pertencem a nenhuma barra — bloco proprio, fetch proprio (nao infla o payload
  // do fluxo; CP fora => o card diz indisponivel, nunca zero).
  const previsaoVencida = usePrevisaoVencida();

  // Redesign Fluxo: inputs do gráfico 30d extraídos pra vars únicas — passadas
  // ao chart E reaproveitadas no KPI "Menor saldo projetado (30d)" via
  // computeRows30d, garantindo que a anotação do gráfico e o KPI derivem do
  // MESMO array de saldo (sem divergência).
  // CP "live" (#61): quando disponível, as SAÍDAS não-energia do Fluxo vêm do CP
  // real do Sync (caixa_real; timing=previsão; valor=líquido) — no lugar do Omie.
  // Fallback honesto: CP indisponível → cai no Omie (estado indisponível, não
  // número errado). Energia segue na posição Zeus (sem dupla contagem).
  const cpOk = v2Ok && fluxoV2.data!.cp_disponivel;
  // #12 (visão completa): a LINHA de saldo soma operacional + ENERGIA (net
  // Zeus por dt_pagamento, pipeline do Calendário de Caixa). As barras seguem
  // operacionais. Campo opcional (deploy skew/cache antigo) → linha degrada
  // pra operacional-only, nunca quebra.
  const series30d = v2Ok
    ? {
        entradasOmie: fluxoV2.data!.entradas_omie_dia,
        saidasOmie: cpOk ? fluxoV2.data!.saidas_cp_dia : fluxoV2.data!.saidas_omie_dia,
        energiaNet: fluxoV2.data!.energia_net_dia ?? [],
        // fix #12b: posicao aberta CCEE por 6DU (segregada pro tooltip; ja
        // somada no energiaNet — a linha bate o card "Saldo Total Estimado").
        posicaoAberta6du: fluxoV2.data!.posicao_aberta_6du_dia ?? [],
        // 13/10 PAR CESSIONARIA: marcas do tooltip (so rotulo).
        marcasCessao: fluxoV2.data!.pares_cessionaria?.marcas_dia ?? [],
      }
    : undefined;
  // Call 08/09 (tooltip mentia a fonte): as duas barras vem de fontes
  // DIFERENTES. Entradas = espelho Omie (canonical_receita, por vencimento).
  // Saidas = CP vivo do Sync (por PREVISAO de pagamento, valor liquido) quando
  // cpOk — rotulo vem de `cp_fonte_label` da response (nunca string fixa; se
  // o backend trocar a fonte, o rotulo acompanha) + `cp_caveat` quando vier.
  // Fallback (CP indisponivel) → a serie e a do espelho Omie e o rotulo diz.
  const rotulos30d: RotulosFonte30d | undefined = v2Ok
    ? cpOk
      ? {
          entradas: ROTULOS_ESPELHO_OMIE.entradas,
          saidas: `Saídas · ${fluxoV2.data!.cp_fonte_label ?? "fonte não informada pelo backend"}`,
          saidasEixo: "por previsão de pagamento",
          saidasCaveat: fluxoV2.data!.cp_caveat ?? null,
          entradasFonte: ROTULOS_ESPELHO_OMIE.entradasFonte,
          saidasFonte: fluxoV2.data!.cp_fonte_label ?? "fonte não informada pelo backend",
        }
      : ROTULOS_ESPELHO_OMIE
    : undefined;
  const hojeIso30d = v2Ok ? fluxoV2.data!.hoje : undefined;
  const menorSaldo30d = useMemo(
    () => _menorSaldo30d(computeRows30d(series30d, diario, hojeIso30d, saldoAtual)),
    [series30d, diario, hojeIso30d, saldoAtual],
  );

  // #30 "Baixar Excel": exporta as MESMAS linhas da tela (computeRows30d com a
  // janela do toggle). Gate anti-mock (review F-30A/B/C): exige fluxo v2 REAL
  // + fora do transiente do toggle + SALDO REAL do /api/v2/saldo; a projeção
  // mensal só entra se o /api/fluxo legado é real (senão a aba é omitida).
  const excel = useMemo(
    () =>
      montarExcelInput({
        v2Ok: !!(v2Ok && series30d && hojeIso30d),
        v2Loading: fluxoV2.isLoading,
        hojeIso: hojeIso30d,
        rows:
          v2Ok && series30d && hojeIso30d
            ? computeRows30d(series30d, diario, hojeIso30d, saldoAtual, janelaDias)
            : [],
        saldoReal: saldoV2.data?.saldo_total_atual,
        legadoReal: state.rows !== null,
        projecao,
        dias: janelaDias,
        fonteSaidas: cpOk ? "cp_sync" : "omie",
      }),
    [
      v2Ok, series30d, diario, hojeIso30d, saldoAtual, janelaDias, projecao,
      cpOk, fluxoV2.isLoading, saldoV2.data, state.rows,
    ],
  );

  // Task #73 + #99: MtM mensal da Posicao Aberta no eixo de CAIXA. Backend
  // devolve { mes_caixa, mes_fornecimento, posicao_aberta_mtm } por item.
  // Frontend mapeia pra chave igual ao formato de `r.mes` ("Mai/26") usando
  // o mes_caixa (que casa com a aggregation client-side por data_pagamento)
  // e mantem o mes_fornecimento como label secundaria pra coluna MES FORN.
  const mtm6m = usePosicaoAbertaMtm6m(mesInicioCaixaIso);
  const mtmByMonth = useMemo(() => {
    const mesesCurto = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    const labelDe = (iso: string) => {
      // ISO "YYYY-MM-DD" — parse via slice numerico pra evitar deslize de
      // fuso (Date(string) interpreta como UTC; -03:00 puxaria pro mes
      // anterior em alguns casos).
      const yyyy = parseInt(iso.slice(0, 4), 10);
      const mm = parseInt(iso.slice(5, 7), 10);
      return `${mesesCurto[mm - 1]}/${String(yyyy).slice(2)}`;
    };
    const out: Record<
      string,
      { mtm: number; mesFornecimentoLabel?: string; volumeMwh?: number; volumeMwm?: number }
    > = {};
    for (const m of mtm6m.data.meses) {
      const keyCaixa = labelDe(m.mes_caixa);
      out[keyCaixa] = {
        mtm: Number(m.posicao_aberta_mtm),
        mesFornecimentoLabel: labelDe(m.mes_fornecimento),
        volumeMwh: Number(m.volume_mwh),
        volumeMwm: Number(m.volume_mwm),
      };
    }
    return out;
  }, [mtm6m.data.meses]);

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-black text-white font-sans">Fluxo de Caixa</h1>
            <CurvaStaleBadge stale={curvaDs?.curva_stale} asOf={curvaDs?.data_ref_zeus} />
          </div>
          <p className="text-white/50 text-sm mt-1">
            Consolidado Atacado + Varejo. Corporativo entra só no DRE.
          </p>
        </div>
        {state.source === "mock" && !state.loading && <DataSourceBadge />}
      </div>

      {state.loading ? (
        <div
          className="text-white/40 text-sm py-12 text-center"
          data-testid="fluxo-loading"
        >
          Carregando dados…
        </div>
      ) : (
        <>
          <SixDuCard
            info={info.info}
            temDadosReais={info.temDadosReais}
            // #62 (b'): estimativa quando o default e heuristica OU quando o
            // 6du NAVEGADO e um proxy alem do calendario oficial.
            isEstimativa={
              (ancora6duIso ? calendario6dus.estimadoByData?.[ancora6duIso] === true : false) ||
              (proximo6du.info?.is_estimativa ?? false)
            }
            // 16/10: fonte do 6du EXIBIDO (navegado) — escolhe o tooltip do badge
            fonte={
              (ancora6duIso ? calendario6dus.fonteByData?.[ancora6duIso] : undefined) ??
              proximo6du.info?.fonte
            }
            nav={nav}
            mesRef={mesRefSelecionado}
            fonteDados={dia6duLive ? "calendario_live" : "fluxo_legado"}
          />

          {/* Task #49: companion do card 6DU — agenda de caixa do ciclo
              inter-6DU por data de vencimento real (Zeus), com movimento
              acumulado pra revelar o vale. #244: ancora no 6du navegado do
              card (1ª linha = 6du exibido), seguindo a navegação prev/next. */}
          <CalendarioCaixaCard ancoraIso={ancora6duIso} />

          {/* UX 03/07 item 4: 3 cards de mesma altura (Entradas/Saídas do mês
              sairam — redundavam com o grafico e o 6DU; NAO recriar por ora). */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6 mb-6 items-stretch">
            {/* Task #132: SaldoAtualCard consome /api/v2/saldo internamente.
                `total` continua como fallback quando o endpoint nao tem
                dado real (freshness > 24h ou erro). awaitingIntegration
                NAO eh forcado — o card decide pelo is_real. */}
            <SaldoAtualCard
              total={saldoAtualTotal}
              sub="Clique para detalhar por banco"
            />
            {/* Redesign Fluxo: KPI do ponto de menor liquidez nos próximos 30
                dias — accent coral quando furar o piso de caixa. Deriva do
                mesmo array de saldo do gráfico 30d (computeRows30d). */}
            <KpiCard
              label="Menor saldo projetado (30d)"
              value={menorSaldo30d ? fmtBRL(menorSaldo30d.valor, { abbreviate: true }) : "—"}
              sub={menorSaldo30d ? menorSaldo30d.offsetLabel : "Aguardando projeção"}
              subTone={menorSaldo30d?.abaixoPiso ? "negative" : "muted"}
              valueTone={menorSaldo30d?.abaixoPiso ? "negative" : "neutral"}
              testId="kpi-menor-saldo-30d"
            />
            <KpiCard
              label="Saldo projetado pós-6DU"
              value={fmtBRL(kpis.saldoProjPosSeisDu, { abbreviate: true })}
              sub={
                liquidoOficial6du.temDadosReais
                  ? // sinal do formatador (review 03/07): liquido NEGATIVO virava
                    // "+-R$ ..." com o "+" na mao — 6DU de saída líquida existe.
                    `${fmtBRLintSigned(liquidoOficial6du.liquido)} na liquidação`
                  : "Aguardando lançamentos para o 6DU"
              }
              subTone={liquidoOficial6du.liquido < 0 ? "negative" : "positive"}
              highlight
              testId="kpi-saldo-proj-pos6du"
            />
          </div>

          {/* Task #129b: 2 graficos empilhados — Veiga rejeitou o concat
              30d+6m num so chart. Eixo Y unico em cada simplifica leitura.
              Task #155: 30d consome SO as 2 series Omie de /api/v2/fluxo
              (CR/CP via canonical_*.data_vencimento). Zeus removido do
              dataset — pico de R$ 9M+ do 6du achatava o fluxo operacional
              diario. Marker visual do proximo 6du desenhado no eixo X via
              `data6du={proximo6du.info?.data}` (data oficial CCEE, nunca
              calcular localmente — paridade #156/#158). Card "Proximo 6du"
              acima continua mostrando o valor Zeus consolidado. */}
          <div className="mb-6 space-y-4">
            {/* 31/08: Itaú Compromissada COMPONDO o fluxo — linha nomeada
                ACIMA do gráfico (a âncora tem DUAS datas: contas de hoje +
                compromissada de data_base; a linha torna isso legível). */}
            <CompromissadaFluxoLinha comp={compFluxo} />
            {/* Toggle de janela do grafico (30/60/90 dias). Acima do grafico,
                segmentado no tema Cortex (selecionado destacado). Default 30. */}
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-white/60 tracking-wider">
                Janela do gráfico
              </span>
              <div
                className="inline-flex rounded-lg border border-white/10 bg-white/5 p-0.5 text-xs"
                role="group"
                aria-label="Janela do gráfico de fluxo"
                data-testid="fluxo-janela-toggle"
              >
                {([30, 60, 90] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setJanelaDias(d)}
                    aria-pressed={janelaDias === d}
                    data-testid={`fluxo-janela-${d}`}
                    className={`px-3 py-1 rounded-md transition-colors ${
                      janelaDias === d
                        ? "bg-indra-turquesa/20 text-indra-turquesa font-semibold"
                        : "text-white/50 hover:text-white/80"
                    }`}
                  >
                    {d} dias
                  </button>
                ))}
              </div>
              {/* #30: exporta o fluxo da janela atual (o que está na tela). */}
              <div className="ml-auto">
                <FluxoExcelButton input={excel.input} motivo={excel.motivo} />
              </div>
            </div>
            {/* UX 03/07 item 3: badge informativo "pré-faxina" REMOVIDO (jargão).
                Fica SÓ o aviso de DEGRADAÇÃO (doutrina #246: fallback nunca é
                mudo): quando o CP do Sync cai, as saídas do gráfico vêm do Omie. */}
            {v2Ok && !cpOk && (
              <span
                className="inline-flex items-center gap-1 text-[10px] tracking-wider px-2 py-0.5 rounded text-amber-200/90 bg-amber-400/10 border border-amber-300/30"
                data-testid="fluxo-cp-degradado"
                title="CP do Sync indisponível — saídas não-energia caem no Omie (último conhecido)."
              >
                Saídas: Omie (CP indisponível)
              </span>
            )}
            <FluxoBarChart30d
              diario={diario}
              series={series30d}
              rotulos={rotulos30d}
              hojeIso={hojeIso30d}
              data6du={proximo6du.info?.data ?? null}
              // Marcadores de TODAS as liquidacoes 6DU na janela (calendario
              // CCEE navegavel) — em 60/90d aparecem 2-3. Datas do backend,
              // nunca calculadas localmente (regra de ferro #156/#158).
              datas6du={calendario6dus.calendario}
              dias={janelaDias}
              saldoInicial={saldoAtual}
            />
            {/* #149/#154: bloco FORA DA SERIE — titulos pendentes com previsao ja
                passada. Nao cabem no tooltip (nao tem dia) nem no subtitulo (nao e
                fato da serie inteira); so aparecem aqui. */}
            {/* #264: o que SAIU das saidas por confirmacao por id (balde) e os ausentes
                da listagem que SEGUEM no saldo, rotulados. So com CP vivo. */}
            {cpOk && (
              <ExistenciaCpCard
                balde={fluxoV2.data!.cp_nao_encontrado_by_id ?? null}
                existencia={fluxoV2.data!.cp_existencia ?? null}
              />
            )}
            <PrevisaoVencidaCard
              data={previsaoVencida.data}
              isLoading={previsaoVencida.isLoading}
              error={previsaoVencida.error}
            />
            <FluxoBarChart6m mensal={mensal} mtmByMonth={mtmByMonth} saldoInicial={saldoAtual} />
          </div>

          <div className="mb-6">
            <SaldoProjetadoTable
              rows={projecao}
              mtmByMonth={mtmByMonth}
              lastSync={mtm6m.data.posicao_aberta_last_sync}
              // #60: as-of do payload do fluxo (a série que alimenta a tabela)
              asOf={fluxoAsOf}
            />
          </div>

          {/* Fix #158: passa data oficial CCEE (useProximo6du) ao invés
              da tabela recalcular via heurística local. Antes o componente
              fazia `calcularProximo6DU(new Date())` direto, errando 1 dia
              útil em meses com feriado móvel (Corpus Christi etc.) — fetch
              consultava o backend com data_alvo errado e tabela ficava
              vazia mesmo havendo trades no 6du oficial. Mesmo padrão de
              fix usado no SixDuCard (#156). */}
          <LiquidacoesNetTabs
            filtroBu="todas"
            dataAlvo={data6duAtual}
            // Task #45: líquido do card 6DU (dust-inclusive) pra reconciliar
            // exato no rodapé da tabela. null quando não há dado real (a tabela
            // filtra dust < R$0,50; o card não → a diferença é o dust).
            cardLiquido6du={info.temDadosReais ? info.info.liquido : null}
          />

          {/* Task #50 Fase C: bloco SEPARADO de posicao aberta projetada,
              renderizado apos as linhas bilaterais. Decisao Veiga: nao
              somar com bilateral; estimativa fica visualmente distinta. */}
          <PosicaoAbertaSection />

          <p className="text-[11px] text-white/40 mt-6 leading-relaxed max-w-3xl">
            As liquidações de energia na CCEE acontecem no 6º dia útil subsequente ao mês
            de fornecimento — daí o nome 6DU. É quando entram os recebimentos dos clientes
            e saem os pagamentos aos geradores. Pode variar para 7DU conforme o
            calendário oficial da CCEE.
          </p>
          {(fluxoV2.data?.seis_dus_estimados?.length ?? 0) > 0 && (
            <p className="text-[11px] text-amber-300/80 mt-2 leading-relaxed max-w-3xl">
              6DU estimado (calendário CCEE ainda não publicado):{" "}
              {fluxoV2.data!.seis_dus_estimados!
                .map((d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`)
                .join(", ")}
              {" "}— data do Zeus quando diverge da regra de dias úteis; substituída
              pela oficial quando a CCEE publicar.
            </p>
          )}
          {(fluxoV2.data?.posicao_aberta_espelho?.length ?? 0) > 0 && (
            <p className="text-[11px] text-amber-300/80 mt-2 leading-relaxed max-w-3xl">
              Posição aberta em estimativa (espelho) nos 6DU{" "}
              {fluxoV2.data!.posicao_aberta_espelho!
                .map((d) => `${d.slice(8, 10)}/${d.slice(5, 7)}`)
                .join(", ")}
              {" "}— calculada dos contratos vigentes sem os clientes excluídos; o feed do
              Zeus é agregado e não permite tirá-los.
            </p>
          )}
          <PrevisaoNaoFirmeNotas marcas={fluxoV2.data?.previsoes_nao_firmes} />

          {/* #84: o Fluxo declara o PRÓPRIO universo — número vivo do backend
              (medir_exclusao a cada request), decisão em destaque, mecânica
              no rodapé. Substitui a nota estática #66 que ninguém lia. */}
          <CoberturaHint cobertura={fluxoV2.data?.cobertura} sujeito="deste fluxo" />
        </>
      )}
    </div>
  );
}
